import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUp, ArrowDown, Sparkles, Wrench, Save, ListTree } from "lucide-react";
import { stepLabel } from "@/lib/rename/recipe";

export default function RecipeTimeline({ recipe, onRemove, onMove, onSaveRecipe, savedRecipes, onLoadRecipe }) {
  return (
    <div className="flex flex-col h-full overflow-y-auto bg-[#070707]/95" data-testid="recipe-timeline">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 sticky top-0 bg-[#070707] z-10">
        <ListTree className="h-4 w-4 text-[#00E5FF]" />
        <span className="text-sm text-white font-medium flex-1">Rename Recipe</span>
        <button
          onClick={onSaveRecipe}
          disabled={!recipe.length}
          data-testid="save-recipe-btn"
          className="text-[11px] flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/15 text-neutral-300 hover:border-[#00E5FF]/50 hover:text-[#00E5FF] transition-colors duration-200 disabled:opacity-40"
        >
          <Save className="h-3 w-3" /> Save
        </button>
      </div>

      <div className="p-4 space-y-2 flex-1">
        {recipe.length === 0 && (
          <p className="text-xs text-neutral-500 font-mono-ui leading-relaxed">
            No steps yet. Use the AI Assistant or Manual Tools — each action is
            added here as a step you can reorder or remove.
          </p>
        )}
        <AnimatePresence initial={false}>
          {recipe.map((step, i) => (
            <motion.div
              key={step.id}
              layout
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-2"
              data-testid={`recipe-step-${i}`}
            >
              <div className={`h-6 w-6 shrink-0 rounded-md flex items-center justify-center ${step.type === "ai" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : "bg-white/10 text-neutral-300"}`}>
                {step.type === "ai" ? <Sparkles className="h-3.5 w-3.5" /> : <Wrench className="h-3.5 w-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white font-mono-ui truncate">{stepLabel(step)}</div>
                {step.scope && step.scope !== "all" && (
                  <div className="text-[10px] text-neutral-500">scope: {step.scope}</div>
                )}
              </div>
              <button onClick={() => onMove(i, -1)} disabled={i === 0} data-testid={`recipe-up-${i}`} className="text-neutral-500 hover:text-white disabled:opacity-20 transition-colors duration-200"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => onMove(i, 1)} disabled={i === recipe.length - 1} data-testid={`recipe-down-${i}`} className="text-neutral-500 hover:text-white disabled:opacity-20 transition-colors duration-200"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button onClick={() => onRemove(step.id)} data-testid={`recipe-remove-${i}`} className="text-neutral-500 hover:text-[#FF3B30] transition-colors duration-200"><X className="h-4 w-4" /></button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {savedRecipes.length > 0 && (
        <div className="border-t border-white/10 p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Saved recipes</div>
          {savedRecipes.map((r) => (
            <button
              key={r.id}
              onClick={() => onLoadRecipe(r)}
              data-testid={`load-recipe-${r.id}`}
              className="w-full text-left text-xs text-neutral-300 font-mono-ui px-3 py-2 rounded-lg border border-white/10 hover:border-[#00E5FF]/50 hover:text-[#00E5FF] transition-colors duration-200"
            >
              {r.name} · {r.steps.length} step(s)
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
